//
// # Theme Color Palette
//
// Provides theme-aware colors for UI overlays (context menus, tooltips, etc.)
// Each color has 4 variants: light, dark, high-contrast, high-contrast-light
//

import { Theme } from "./theme";

// Color values for each theme
// Order: [light, dark, high-contrast, high-contrast-light]
interface PaletteColors {
  // Background colors
  background: [string, string, string, string];
  backgroundHover: [string, string, string, string];

  // Border colors
  border: [string, string, string, string];

  // Text colors
  text: [string, string, string, string];
  textMuted: [string, string, string, string];
  textHeader: [string, string, string, string];
  textTarget: [string, string, string, string];
  textSection: [string, string, string, string];
  textParam: [string, string, string, string];

  // Shadow
  boxShadow: [string, string, string, string];
}

// The color definitions for all themes
// Index 0: light
// Index 1: dark
// Index 2: high-contrast (dark)
// Index 3: high-contrast-light
const colors: PaletteColors = {
  // Background colors
  background: [
    "#ffffff", // light: white
    "#2d2d30", // dark: VSCode dark gray
    "#000000", // high-contrast: pure black
    "#ffffff", // high-contrast-light: pure white
  ],
  backgroundHover: [
    "#e8e8e8", // light: light gray
    "#094771", // dark: VSCode selection blue
    "#0078d4", // high-contrast: bright blue
    "#0078d4", // high-contrast-light: bright blue
  ],

  // Border colors
  border: [
    "#d4d4d4", // light: light gray
    "#454545", // dark: medium gray
    "#6fc3df", // high-contrast: cyan
    "#0078d4", // high-contrast-light: blue
  ],

  // Text colors
  text: [
    "#333333", // light: dark gray
    "#cccccc", // dark: light gray
    "#ffffff", // high-contrast: pure white
    "#000000", // high-contrast-light: pure black
  ],
  textMuted: [
    "#666666", // light: medium gray
    "#9cdcfe", // dark: VSCode light blue
    "#9cdcfe", // high-contrast: light blue
    "#0451a5", // high-contrast-light: dark blue
  ],
  textHeader: [
    "#0451a5", // light: dark blue
    "#569cd6", // dark: VSCode blue
    "#6fc3df", // high-contrast: cyan
    "#0451a5", // high-contrast-light: dark blue
  ],
  textTarget: [
    "#267f99", // light: teal
    "#9cdcfe", // dark: light blue
    "#9cdcfe", // high-contrast: light blue
    "#267f99", // high-contrast-light: teal
  ],
  textSection: [
    "#795e26", // light: brown
    "#dcdcaa", // dark: VSCode yellow
    "#ffd700", // high-contrast: gold
    "#795e26", // high-contrast-light: brown
  ],
  textParam: [
    "#a31515", // light: dark red
    "#ce9178", // dark: VSCode orange
    "#f48771", // high-contrast: salmon
    "#a31515", // high-contrast-light: dark red
  ],

  // Shadow
  boxShadow: [
    "0 4px 12px rgba(0,0,0,0.15)", // light: subtle shadow
    "0 4px 12px rgba(0,0,0,0.5)", // dark: strong shadow
    "none", // high-contrast: no shadow for clarity
    "0 4px 12px rgba(0,0,0,0.25)", // high-contrast-light: medium shadow
  ],
};

// Get theme index for palette lookup
function getThemeIndex(theme: Theme): 0 | 1 | 2 | 3 {
  switch (theme) {
    case Theme.Light:
      return 0;
    case Theme.Dark:
      return 1;
    case Theme.HighContrast:
      return 2;
    case Theme.HighContrastLight:
      return 3;
    default:
      return 0;
  }
}

// The public palette interface - returns current theme's color
export const Palette = {
  // Get a specific color for the current theme
  get: (colorName: keyof PaletteColors): string => {
    const index = getThemeIndex(Theme.get());
    return colors[colorName][index];
  },

  // Get all colors for the current theme (for bulk styling)
  getAll: (): { [K in keyof PaletteColors]: string } => {
    const index = getThemeIndex(Theme.get());
    return {
      background: colors.background[index],
      backgroundHover: colors.backgroundHover[index],
      border: colors.border[index],
      text: colors.text[index],
      textMuted: colors.textMuted[index],
      textHeader: colors.textHeader[index],
      textTarget: colors.textTarget[index],
      textSection: colors.textSection[index],
      textParam: colors.textParam[index],
      boxShadow: colors.boxShadow[index],
    };
  },

  // Generate CSS for a stylesheet that uses CSS variables
  // This allows dynamic theme switching without recreating elements
  generateCssVariables: (): string => {
    const p = Palette.getAll();
    return `
      --palette-background: ${p.background};
      --palette-background-hover: ${p.backgroundHover};
      --palette-border: ${p.border};
      --palette-text: ${p.text};
      --palette-text-muted: ${p.textMuted};
      --palette-text-header: ${p.textHeader};
      --palette-text-target: ${p.textTarget};
      --palette-text-section: ${p.textSection};
      --palette-text-param: ${p.textParam};
      --palette-box-shadow: ${p.boxShadow};
    `;
  },
};
